import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Alert, Skeleton, Space, Tabs } from "antd";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Breadcrumbs } from "@/shared/components/Breadcrumbs";
import { useEmployeeNames } from "@/shared/hooks/useEmployeeNames";
import { usePermission } from "@/shared/hooks/usePermission";
import { useRecentlyViewed } from "@/shared/hooks/useRecentlyViewed";
import { EmployeeAuditTab } from "../components/EmployeeAuditTab";
import { EmployeeOverviewTab } from "../components/EmployeeOverviewTab";
import { EmployeeProfileHeader } from "../components/EmployeeProfileHeader";
import { EmployeeZonesTab } from "../components/EmployeeZonesTab";
import {
	EmployeeAbsencesTab,
	EmployeeAttendanceTab,
	EmployeeDevicesTab,
	EmployeeLeadsTab,
	EmployeeShiftsTab,
	EmployeeTasksTab,
} from "../components/employeeTabs";
import { useDepartmentOptions, useEmployeeDetail, usePositionOptions } from "../hooks/useEmployees";
import type { EmployeeDetail } from "../types";
import {
	EMPLOYEE_TAB_PERMISSION,
	EMPLOYEE_TABS,
	type EmployeeTab,
	employeeTabLabel,
	resolveTab,
} from "../utils/tabs";

/**
 * Xodim profili — burg'ulash zanjirining ilk manzili.
 *
 * ⚠️ `useParams({ strict: false })` — marshrut obyekti `routeTree.tsx`
 * da (poydevor fayli) va uni import qilish shu sahifadan poydevorga
 * qayta bog'lanish yaratardi (`LeadDetailPage` naqshi).
 *
 * ⚠️ Bo'lim va lavozim nomlari `useDepartmentOptions()` /
 * `usePositionOptions()` KESHIDAN olinadi — `EmployeesPage` aynan shu
 * query kalitlarini ishlatadi, ya'ni ro'yxatdan kelgan foydalanuvchi
 * uchun qo'shimcha so'rov umuman ketmaydi.
 */
/**
 * Yorliq → tarkib.
 *
 * ⚠️ Bu funksiya `Tabs.items` ichida chaqiriladi, lekin `destroyOnHidden`
 * tufayli FAQAT faol yorliqning elementi mount bo'ladi — olti
 * `usePaginatedQuery` bir vaqtda so'rov yubormaydi.
 */
function renderTab(tab: EmployeeTab, employee: EmployeeDetail) {
	const employeeId = employee.id;

	switch (tab) {
		case "umumiy":
			// ⚠️ «Umumiy» yorlig'i TO'LIQ obyektni oladi — yon ustundagi
			// `RiskCard` `riskScore`/`riskFlag`/`riskAcknowledgedAt` ni
			// o'qiydi va ular allaqachon `EmployeeDetail` da bor, ya'ni
			// ikkinchi so'rov keraksiz.
			return <EmployeeOverviewTab employee={employee} />;
		case "davomat":
			return <EmployeeAttendanceTab employeeId={employeeId} />;
		case "smenalar":
			return <EmployeeShiftsTab employeeId={employeeId} />;
		case "arizalar":
			return <EmployeeAbsencesTab employeeId={employeeId} />;
		case "topshiriqlar":
			return <EmployeeTasksTab employeeId={employeeId} />;
		case "lidlar":
			return <EmployeeLeadsTab employeeId={employeeId} />;
		case "qurilmalar":
			return <EmployeeDevicesTab employeeId={employeeId} />;
		case "hududlar":
			return <EmployeeZonesTab employeeId={employeeId} />;
		case "audit":
			return <EmployeeAuditTab employeeId={employeeId} />;
	}
}

export function EmployeeDetailPage() {
	const { t } = useTranslation("employees");
	const params = useParams({ strict: false }) as { id?: string };
	const search = useSearch({ strict: false }) as { tab?: string };
	const navigate = useNavigate();
	const { can } = usePermission();

	// ⚠️ Yorliqlar ruxsatga qarab FILTRLANADI, `disabled` qilinmaydi:
	// ko'rinadigan, lekin har bosishda 403 beradigan yorliq foydalanuvchiga
	// tizim buzuq degan taassurot berardi.
	const yorliqlar = useMemo(
		() =>
			EMPLOYEE_TABS.filter((tab) => {
				const ruxsat = EMPLOYEE_TAB_PERMISSION[tab];
				return !ruxsat || can(ruxsat[0], ruxsat[1]);
			}),
		[can]
	);

	// ⚠️ `resolveTab` RUXSAT ETILGANLAR ro'yxatini oladi — usiz `?tab=audit`
	// bilan kelgan `audit:read` siz foydalanuvchi mavjud bo'lmagan yorliqni
	// faol deb ko'rsatib, `Tabs` ni BO'SH qoldirardi.
	const activeTab = resolveTab(search.tab, yorliqlar);

	// ⚠️ `replace: true` — yorliqni har bosish tarixga yozilsa, «orqaga»
	// tugmasi foydalanuvchini profildan chiqarish o'rniga yorliqlar
	// bo'ylab orqaga surardi.
	const handleTabChange = useCallback(
		(key: string) => {
			navigate({
				to: ".",
				search: (eski: Record<string, unknown>) => ({ ...eski, tab: key }),
				replace: true,
			});
		},
		[navigate]
	);
	const { data: employee, isLoading, isError } = useEmployeeDetail(params.id);
	const { remember } = useRecentlyViewed();

	const { data: departments = [] } = useDepartmentOptions();
	const { data: positions = [] } = usePositionOptions();
	const managerNames = useEmployeeNames([employee?.managerId]);

	const departmentName = useMemo(
		() => departments.find((item) => item.id === employee?.departmentId)?.name,
		[departments, employee?.departmentId]
	);
	const positionName = useMemo(
		() => positions.find((item) => item.id === employee?.positionId)?.name,
		[positions, employee?.positionId]
	);

	// ⚠️ `employee` kelgandan KEYIN eslanadi — aks holda ro'yxatda
	// yuklanish paytidagi bo'sh yorliq qolardi (`LeadDetailPage` naqshi).
	useEffect(() => {
		if (employee) {
			remember({
				path: `/employees/${employee.id}`,
				label: t("detailPage.recentlyViewed", { ism: employee.fullName }),
			});
		}
	}, [employee, remember, t]);

	if (isLoading) {
		return <Skeleton active />;
	}

	if (isError || !employee) {
		return <Alert type="error" showIcon message={t("detailPage.loadError")} />;
	}

	return (
		<Space direction="vertical" size="middle" className="w-full">
			<Breadcrumbs
				items={[
					{ label: t("breadcrumb.organization") },
					{ label: t("breadcrumb.employees"), to: "/employees" },
					{ label: employee.fullName },
				]}
			/>

			<EmployeeProfileHeader
				employee={employee}
				departmentName={departmentName}
				positionName={positionName}
				managerName={employee.managerId ? managerNames.get(employee.managerId) : undefined}
			/>

			{/* ⚠️ `destroyOnHidden` — usiz ko'rilgan yorliq mount'da qolib,
			    olti `usePaginatedQuery` fokus qaytganda BIRGA refetch qilardi. */}
			<Tabs
				activeKey={activeTab}
				onChange={handleTabChange}
				destroyOnHidden
				items={yorliqlar.map((tab) => ({
					key: tab,
					label: employeeTabLabel(t, tab),
					children: renderTab(tab, employee),
				}))}
			/>
		</Space>
	);
}
